import { useQuery } from '@tanstack/react-query';
import { useCompany } from '../hooks/useCompany';
import { useAuth } from '../hooks/useAuth';
import { dashboardAPI, consolidationAPI } from '../lib/api';
import { Link } from 'react-router-dom';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Users,
  Building2,
  AlertTriangle,
  Receipt,
  Wallet,
  FileText,
  Clock,
  BarChart3,
  ArrowRight,
  Sparkles,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Info,
  Banknote,
  Shield,
  Package,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const CHART_COLORS = ['#0066B3', '#00A651', '#006837', '#009B77', '#0A2240'];

// Spells out the currency code ("USD $1,500" / "LRD $10,000") instead of
// relying on "$" vs "L$" alone to distinguish them.
const formatCurrency = (value, currency = 'USD') => {
  const amount = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);
  return `${currency} $${amount}`;
};

const formatNumber = (value) => {
  return new Intl.NumberFormat('en-US').format(value || 0);
};

function StatusBadge({ status }) {
  const styles = {
    draft: 'status-draft',
    pending: 'status-pending',
    submitted: 'status-pending',
    approved: 'status-approved',
    posted: 'status-posted',
    rejected: 'status-rejected',
  };

  return (
    <span className={`status-badge ${styles[status?.toLowerCase()] || 'status-draft'}`}>
      {status}
    </span>
  );
}

// Simulated AI Operations Brief data (this would come from an API in production)
function useAIOperationsBrief() {
  return useQuery({
    queryKey: ['ai-operations-brief'],
    queryFn: async () => {
      // Simulated AI brief data
      return {
        summary: "The current operational data indicates a critical cash position with $0 USD and L$0 LRD across all cash points (vault and teller). Additionally, one stock item is running low, and there are no active sessions or pending allocations despite all four tellers being active.",
        insights: [
          "Immediately address the $0 cash position across all cash points to ensure operational continuity and customer service availability.",
          "Activate sessions or allocate tasks to the four active tellers to optimize their productivity and address any potential service delays.",
          "Investigate and replenish the low stock item to prevent it from going out of stock, as there are no pending allocations to resolve this issue."
        ],
        alerts: [
          { type: 'cash', severity: 'warning', message: '1 item(s) running low on stock.' }
        ],
        metrics: {
          vault: { totalUSD: 0, totalLRD: 0 },
          tellers: { active: 4, total: 4, totalCashUSD: 0, totalCashLRD: 0, activeSessions: 0 },
          stock: { totalItems: 1, lowStock: 1, outOfStock: 0 },
          allocations: { pending: 0, todayTotal: 0 }
        },
        generatedAt: new Date().toISOString()
      };
    },
    staleTime: 55000,
    refetchInterval: 60000,
  });
}

export default function Dashboard() {
  const { user } = useAuth();
  const { selectedCompany, isParent } = useCompany();

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard-stats', selectedCompany?.id],
    queryFn: () => dashboardAPI.getStats(selectedCompany?.id).then((res) => res.data),
    enabled: !!selectedCompany?.id,
    refetchInterval: 30000,
  });

  const { data: activity = [], isLoading: activityLoading } = useQuery({
    queryKey: ['dashboard-activity', selectedCompany?.id],
    queryFn: () => dashboardAPI.getActivity(selectedCompany?.id).then((res) => res.data),
    enabled: !!selectedCompany?.id,
  });

  const { data: consolidation } = useQuery({
    queryKey: ['consolidation', selectedCompany?.id],
    queryFn: () => consolidationAPI.getData(selectedCompany?.id).then((res) => res.data),
    enabled: !!selectedCompany?.id && isParent,
  });

  const { data: aiOperationsBrief, isLoading: aiLoading, refetch: refetchAI, isFetching: aiRefetching } = useAIOperationsBrief();

  // Chart data - shows actual data or empty state for production
  const revenueData = stats?.monthly_data || [
    { month: 'Jan', revenue: 0, expenses: 0 },
    { month: 'Feb', revenue: 0, expenses: 0 },
    { month: 'Mar', revenue: 0, expenses: 0 },
    { month: 'Apr', revenue: 0, expenses: 0 },
    { month: 'May', revenue: 0, expenses: 0 },
    { month: 'Jun', revenue: 0, expenses: 0 },
  ];

  const expenseBreakdown = stats?.expense_breakdown || [
    { category: 'No Data', amount: 0 },
  ];

  // Chart shows the USD figure only -- a bar can't meaningfully mix two
  // currencies in one value; see Consolidation.js for the full USD+LRD table.
  const subsidiaryPerformance = consolidation?.subsidiaries?.map((sub) => ({
    name: sub.name.replace('DEA ', '').replace('DFS Financial Services', 'DFS'),
    revenue: sub.revenue_usd || 0,
    profit: sub.profit_usd || 0,
  })) || [];

  // USD figure is the primary KPI value; LRD (when nonzero) becomes a
  // secondary sub-line rather than being added into the USD number.
  const kpis = [
    {
      label: 'Total Revenue',
      value: formatCurrency(stats?.total_revenue_usd),
      subValue: stats?.total_revenue_lrd > 0 ? formatCurrency(stats.total_revenue_lrd, 'LRD') : null,
      change: `+${stats?.revenue_change?.toFixed(1) || 0}%`,
      positive: true,
      icon: DollarSign,
    },
    {
      label: 'Total Expenses',
      value: formatCurrency(stats?.total_expenses_usd),
      subValue: stats?.total_expenses_lrd > 0 ? formatCurrency(stats.total_expenses_lrd, 'LRD') : null,
      change: `+${stats?.expense_change?.toFixed(1) || 0}%`,
      positive: false,
      icon: Receipt,
    },
    {
      label: 'Net Income',
      value: formatCurrency(stats?.net_income_usd),
      subValue: stats?.net_income_lrd ? formatCurrency(stats.net_income_lrd, 'LRD') : null,
      // No margin is meaningful with zero revenue -- omit the badge rather
      // than showing the literal string "NaN% margin" (a division by zero
      // that used to get hardcoded as text instead of just hidden).
      change: stats?.total_revenue_usd > 0
        ? `${((stats?.net_income_usd / stats?.total_revenue_usd) * 100).toFixed(1)}% margin`
        : null,
      icon: TrendingUp,
    },
    {
      label: 'Cash Position',
      value: formatCurrency(stats?.cash_position_usd),
      subValue: stats?.cash_position_lrd > 0 ? formatCurrency(stats.cash_position_lrd, 'LRD') : null,
      icon: Wallet,
    },
    {
      label: 'Total Assets',
      value: formatCurrency(stats?.total_assets_usd),
      subValue: stats?.total_assets_lrd > 0 ? formatCurrency(stats.total_assets_lrd, 'LRD') : null,
      icon: Building2,
    },
    {
      label: 'Total Liabilities',
      value: formatCurrency(stats?.total_liabilities_usd),
      subValue: stats?.total_liabilities_lrd > 0 ? formatCurrency(stats.total_liabilities_lrd, 'LRD') : null,
      icon: FileText,
    },
    {
      label: 'Employees',
      value: formatNumber(stats?.employee_count),
      icon: Users,
    },
    {
      label: 'Pending Approvals',
      value: formatNumber(stats?.pending_approvals),
      icon: AlertTriangle,
    },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            Welcome back, {user?.first_name || 'User'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {selectedCompany?.name || 'Select a company'} - Dashboard Overview
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted px-3 py-1.5 rounded-full">
          <Clock className="h-3 w-3" />
          Last updated: {new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </div>
      </div>

      {/* KPI Grid */}
      {statsLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="h-4 w-24 bg-muted rounded mb-2"></div>
              <div className="h-8 w-32 bg-muted rounded"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-6">
          {kpis.map((kpi, idx) => {
            const Icon = kpi.icon;
            return (
              <div key={idx} data-testid={`kpi-${kpi.label.toLowerCase().replace(/\s/g, '-')}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Icon className="h-4 w-4 text-muted-foreground" />
                  <span className="kpi-label">{kpi.label}</span>
                </div>
                <span className="kpi-value text-foreground">{kpi.value}</span>
                {kpi.change && (
                  <span
                    className={`ml-2 text-xs font-medium ${
                      kpi.positive ? 'text-green-600' : kpi.positive === false ? 'text-red-600' : 'text-muted-foreground'
                    }`}
                  >
                    {kpi.change}
                  </span>
                )}
                {kpi.subValue && (
                  <div className="text-xs font-medium text-blue-600">{kpi.subValue}</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="border-t border-border/50" />

      {/* Charts Section */}
      <div className="grid gap-8 lg:grid-cols-3">
        {/* Revenue vs Expenses Chart */}
        <div className="lg:col-span-2">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <div>
              <h3 className="text-base font-semibold text-foreground">Revenue vs Expenses</h3>
              <p className="text-xs text-muted-foreground">Monthly comparison for the last 6 months</p>
            </div>
            <Link
              to="/finance/journals"
              className="text-xs text-primary hover:underline flex items-center gap-1"
              data-testid="link-view-reports"
            >
              View Reports <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenueData}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHART_COLORS[0]} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={CHART_COLORS[0]} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorExpenses" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHART_COLORS[1]} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={CHART_COLORS[1]} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '6px',
                  }}
                  formatter={(value) => [formatCurrency(value), '']}
                />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke={CHART_COLORS[0]}
                  fill="url(#colorRevenue)"
                  strokeWidth={2}
                  name="Revenue"
                />
                <Area
                  type="monotone"
                  dataKey="expenses"
                  stroke={CHART_COLORS[1]}
                  fill="url(#colorExpenses)"
                  strokeWidth={2}
                  name="Expenses"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Expense Breakdown */}
        <div>
          <div className="mb-4">
            <h3 className="text-base font-semibold text-foreground">Expense Breakdown</h3>
            <p className="text-xs text-muted-foreground">Distribution by category</p>
          </div>
          <div className="h-[180px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={expenseBreakdown}
                  cx="50%"
                  cy="50%"
                  innerRadius={45}
                  outerRadius={70}
                  paddingAngle={2}
                  dataKey="amount"
                >
                  {expenseBreakdown.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [formatCurrency(value), '']} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 space-y-2">
            {expenseBreakdown.map((item, index) => (
              <div key={item.category} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <div
                    className="h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                  />
                  <span className="text-muted-foreground">{item.category}</span>
                </div>
                <span className="font-medium font-mono text-sm">{formatCurrency(item.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="border-t border-border/50" />

      {/* AI Operations Brief Section */}
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <div>
              <h3 className="text-base font-semibold text-foreground">AI Operations Brief</h3>
              <p className="text-xs text-muted-foreground">
                DFS Financial Services real-time analysis
                {aiOperationsBrief?.generatedAt && (
                  <span className="ml-2">
                    Updated {new Date(aiOperationsBrief.generatedAt).toLocaleTimeString()}
                  </span>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={() => refetchAI()}
            disabled={aiRefetching}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md hover:bg-muted transition-colors disabled:opacity-50"
            data-testid="button-refresh-ai"
          >
            <RefreshCw className={`h-4 w-4 ${aiRefetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {aiLoading ? (
          <div className="space-y-3">
            <div className="h-16 w-full bg-muted rounded animate-pulse" />
            <div className="grid gap-3 md:grid-cols-5">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-20 bg-muted rounded animate-pulse" />
              ))}
            </div>
          </div>
        ) : aiOperationsBrief ? (
          <div className="space-y-4">
            {/* AI Summary */}
            <div className="p-4 bg-primary/5 rounded-lg border border-primary/10">
              <p className="text-sm text-foreground" data-testid="text-ai-summary">
                {aiOperationsBrief.summary}
              </p>
            </div>

            {/* Alerts */}
            {aiOperationsBrief.alerts?.length > 0 && (
              <div className="space-y-2">
                {aiOperationsBrief.alerts.map((alert, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
                      alert.severity === 'critical'
                        ? 'bg-destructive/10 text-destructive'
                        : alert.severity === 'warning'
                        ? 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400'
                        : 'bg-muted text-muted-foreground'
                    }`}
                    data-testid={`alert-${alert.type}-${idx}`}
                  >
                    {alert.severity === 'critical' ? (
                      <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    ) : alert.severity === 'warning' ? (
                      <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                    ) : (
                      <Info className="h-4 w-4 flex-shrink-0" />
                    )}
                    <span>{alert.message}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Key Metrics Grid */}
            <div className="grid gap-4 md:grid-cols-5">
              <div className="p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Banknote className="h-4 w-4 text-muted-foreground" />
                  <p className="text-xs text-muted-foreground">Vault Cash</p>
                </div>
                <p className="text-sm font-semibold">{formatCurrency(aiOperationsBrief.metrics?.vault?.totalUSD || 0)}</p>
                <p className="text-xs text-muted-foreground">{formatCurrency(aiOperationsBrief.metrics?.vault?.totalLRD || 0, 'LRD')}</p>
              </div>
              <div className="p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Wallet className="h-4 w-4 text-muted-foreground" />
                  <p className="text-xs text-muted-foreground">Teller Cash</p>
                </div>
                <p className="text-sm font-semibold">{formatCurrency(aiOperationsBrief.metrics?.tellers?.totalCashUSD || 0)}</p>
                <p className="text-xs text-muted-foreground">{formatCurrency(aiOperationsBrief.metrics?.tellers?.totalCashLRD || 0, 'LRD')}</p>
              </div>
              <div className="p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <p className="text-xs text-muted-foreground">Active Tellers</p>
                </div>
                <p className="text-sm font-semibold">{aiOperationsBrief.metrics?.tellers?.active || 0} / {aiOperationsBrief.metrics?.tellers?.total || 0}</p>
                <p className="text-xs text-muted-foreground">{aiOperationsBrief.metrics?.tellers?.activeSessions || 0} sessions</p>
              </div>
              <div className="p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Package className="h-4 w-4 text-muted-foreground" />
                  <p className="text-xs text-muted-foreground">Stock Status</p>
                </div>
                <p className="text-sm font-semibold">{aiOperationsBrief.metrics?.stock?.totalItems || 0} Items</p>
                <p className="text-xs text-muted-foreground">
                  {aiOperationsBrief.metrics?.stock?.lowStock > 0 && (
                    <span className="text-yellow-600 dark:text-yellow-400">{aiOperationsBrief.metrics?.stock?.lowStock} low</span>
                  )}
                  {aiOperationsBrief.metrics?.stock?.lowStock === 0 && 'All stocked'}
                </p>
              </div>
              <div className="p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Shield className="h-4 w-4 text-muted-foreground" />
                  <p className="text-xs text-muted-foreground">Allocations</p>
                </div>
                <p className="text-sm font-semibold">{aiOperationsBrief.metrics?.allocations?.todayTotal || 0} today</p>
                <p className="text-xs text-muted-foreground">{aiOperationsBrief.metrics?.allocations?.pending || 0} pending</p>
              </div>
            </div>

            {/* AI Insights */}
            {aiOperationsBrief.insights?.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">AI Insights</p>
                <div className="grid gap-2 md:grid-cols-2">
                  {aiOperationsBrief.insights.map((insight, idx) => (
                    <div key={idx} className="flex items-start gap-2 p-3 bg-muted/30 rounded-lg" data-testid={`insight-${idx}`}>
                      <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-foreground">{insight}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="p-4 text-center text-sm text-muted-foreground">
            Unable to load AI Operations Brief
          </div>
        )}
      </div>

      <div className="border-t border-border/50" />

      {/* Activity and Quick Actions */}
      <div className="grid gap-8 lg:grid-cols-2">
        {/* Recent Activity */}
        <div>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <div>
              <h3 className="text-base font-semibold text-foreground">Recent Activity</h3>
              <p className="text-xs text-muted-foreground">Latest transactions and updates</p>
            </div>
            <Link
              to="/finance/journals"
              className="text-xs text-primary hover:underline flex items-center gap-1"
              data-testid="link-view-all-activity"
            >
              View All <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="space-y-0">
            {activityLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 py-3 animate-pulse">
                  <div className="h-8 w-8 bg-muted rounded-md"></div>
                  <div className="flex-1">
                    <div className="h-4 w-32 bg-muted rounded mb-1"></div>
                    <div className="h-3 w-24 bg-muted rounded"></div>
                  </div>
                </div>
              ))
            ) : activity.length > 0 ? (
              activity.map((item, index) => (
                <div
                  key={item.id}
                  className={`flex items-center gap-3 py-2.5 ${
                    index !== activity.length - 1 ? 'border-b border-border/30' : ''
                  }`}
                >
                  <div className="flex h-8 w-8 items-center justify-center rounded-md bg-muted/50">
                    <Receipt className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{item.description}</p>
                    <p className="text-xs text-muted-foreground">
                      {item.type} {item.amount ? `- ${formatCurrency(item.amount)}` : ''}
                    </p>
                  </div>
                  <StatusBadge status={item.status} />
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No recent activity
              </p>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div>
          <div className="mb-4">
            <h3 className="text-base font-semibold text-foreground">Quick Actions</h3>
            <p className="text-xs text-muted-foreground">Common tasks and shortcuts</p>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <Link
              to="/finance/journals"
              className="flex items-center gap-3 p-3 rounded-lg hover-elevate"
              data-testid="quick-action-new-journal"
            >
              <Receipt className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">New Journal Entry</p>
                <p className="text-xs text-muted-foreground">Record a transaction</p>
              </div>
            </Link>
            <Link
              to="/hr/employees"
              className="flex items-center gap-3 p-3 rounded-lg hover-elevate"
              data-testid="quick-action-add-employee"
            >
              <Users className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Add Employee</p>
                <p className="text-xs text-muted-foreground">Create employee record</p>
              </div>
            </Link>
            <Link
              to="/finance/budgets"
              className="flex items-center gap-3 p-3 rounded-lg hover-elevate"
              data-testid="quick-action-create-budget"
            >
              <Wallet className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Budget Planning</p>
                <p className="text-xs text-muted-foreground">Manage budgets</p>
              </div>
            </Link>
            <Link
              to="/consolidation"
              className="flex items-center gap-3 p-3 rounded-lg hover-elevate"
              data-testid="quick-action-view-reports"
            >
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">View Reports</p>
                <p className="text-xs text-muted-foreground">Financial statements</p>
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* Subsidiary Performance (Parent company only) */}
      {isParent && (
        <>
          <div className="border-t border-border/50" />
          <div>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
              <div>
                <h3 className="text-base font-semibold text-foreground">Subsidiary Performance</h3>
                <p className="text-xs text-muted-foreground">Comparative overview of all subsidiaries</p>
              </div>
              <Link
                to="/consolidation"
                className="text-xs text-primary hover:underline flex items-center gap-1"
                data-testid="link-full-comparison"
              >
                Full Comparison <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={subsidiaryPerformance} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                  <XAxis
                    type="number"
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                  />
                  <YAxis
                    dataKey="name"
                    type="category"
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    width={100}
                  />
                  <Tooltip formatter={(value) => [formatCurrency(value), '']} />
                  <Bar dataKey="revenue" fill={CHART_COLORS[0]} radius={[0, 4, 4, 0]} name="Revenue" />
                  <Bar dataKey="profit" fill={CHART_COLORS[1]} radius={[0, 4, 4, 0]} name="Profit" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
